import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2, UserMinus, Users, KeyRound, Copy, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import DetailDrawer from "@/components/DetailDrawer";
import RecordsPanel from "@/components/records/RecordsPanel";
import RateHistoryPanel from "@/components/hr/RateHistoryPanel";
import LeaveBalancePanel from "@/components/hr/LeaveBalancePanel";
import AttributeHistoryPanel from "@/components/hr/AttributeHistoryPanel";
import AsOfRecordPanel from "@/components/hr/AsOfRecordPanel";
import AssignmentsPanel from "@/components/hr/AssignmentsPanel";
import { toast } from "sonner";
import { SkeletonTable } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";

export default function Members() {
  const { companyId } = useParams();
  const [params, setParams] = useSearchParams();
  const { has } = useCompany();
  const { user } = useAuth();
  const [data, setData] = React.useState(null);
  const [roles, setRoles] = React.useState([]);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [inviteRoleId, setInviteRoleId] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [drawerUserId, setDrawerUserId] = React.useState(params.get("open"));

  const canManage = has("members.manage");

  const load = React.useCallback(async () => {
    if (!companyId) return;
    const [{ data: m }, { data: r }] = await Promise.all([
      api.get(`/companies/${companyId}/members`),
      api.get(`/companies/${companyId}/roles`),
    ]);
    setData(m);
    setRoles(r.roles);
    if (!inviteRoleId && r.roles.length) {
      // Default invite role: prefer Staff, else last non-Owner
      const staff = r.roles.find((x) => x.preset_key === "staff");
      setInviteRoleId((staff || r.roles[r.roles.length - 1])?.id || "");
    }
  }, [companyId, inviteRoleId]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const on = (e) => {
      const t = e.detail?.type;
      if (t && (t.startsWith("member.") || t.startsWith("invite."))) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  React.useEffect(() => { setDrawerUserId(params.get("open")); }, [params]);

  async function sendInvite(e) {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteRoleId || busy) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/companies/${companyId}/members/invite`, {
        email: inviteEmail.trim(), role_id: inviteRoleId,
      });
      const map = { added: "Added", invited: "Invite sent",
                    already_member: "Already a member", already_invited: "Already invited" };
      toast.success(map[r.status] || "Done");
      setInviteEmail("");
      setInviteOpen(false);
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function revokeInvite(id) {
    if (!window.confirm("Revoke this invite?")) return;
    try {
      await api.delete(`/companies/${companyId}/invites/${id}`);
      toast.success("Removed");
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail));
    }
  }

  function openDrawer(userId) {
    setDrawerUserId(userId);
    const p = new URLSearchParams(params);
    p.set("open", userId);
    setParams(p, { replace: true });
  }
  function closeDrawer() {
    setDrawerUserId(null);
    const p = new URLSearchParams(params);
    p.delete("open");
    setParams(p, { replace: true });
  }

  if (!data) {
    return (
      <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="members-loading">
        <div className="micro-label">People</div>
        <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Members</h1>
        <div className="mt-4"><SkeletonTable rows={6} cols={4} /></div>
      </div>
    );
  }

  const seat = data.seat || { used: 0, pending: 0, limit: 0 };
  const activeMember = drawerUserId ? data.members.find((m) => m.user_id === drawerUserId) : null;

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="members-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="micro-label">People</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Members</h1>
          <div className="mt-1 text-[12px] text-brand-muted">
            <span className="mono-num text-brand-ink">{seat.used}</span> active
            {" · "}
            <span className="mono-num text-brand-ink">{seat.pending}</span> pending
            {" · limit "}
            <span className="mono-num text-brand-ink">{seat.limit}</span>
          </div>
        </div>
        {canManage && (
          <button onClick={() => setInviteOpen(true)} className="btn-violet" data-testid="invite-open">
            <Plus size={13} /> Invite member
          </button>
        )}
      </div>

      {inviteOpen && (
        <form onSubmit={sendInvite} className="mt-4 card-panel" data-testid="invite-form">
          <div className="card-panel-head">
            <div className="text-[13px] font-semibold text-brand-ink">Invite by email</div>
            <button type="button" className="btn-ghost" onClick={() => setInviteOpen(false)}
                    data-testid="invite-cancel">Cancel</button>
          </div>
          <div className="card-panel-body grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="micro-label">Email</label>
              <input className="field-input mt-1" type="email" value={inviteEmail}
                     onChange={(e)=>setInviteEmail(e.target.value)} required
                     data-testid="invite-email" />
            </div>
            <div>
              <label className="micro-label">Role</label>
              <select className="field-input mt-1" value={inviteRoleId} onChange={(e) => setInviteRoleId(e.target.value)}
                      data-testid="invite-role">
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <button type="submit" className="btn-violet" disabled={busy || !inviteEmail.trim()}
                      data-testid="invite-submit">
                {busy ? "…" : "Send invite"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Members table */}
      <div className="mt-4 card-panel overflow-hidden">
        <div className="table-scroll"><table className="fable-table" data-testid="members-table">
          <thead>
            <tr>
              <th style={{width: "36%"}}>Name</th>
              <th>Email</th>
              <th style={{width: "12%"}}>Role</th>
              <th style={{width: "14%"}}>Last login</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.user_id} data-clickable="true" onClick={() => openDrawer(m.user_id)}
                  data-testid={`member-row-${m.user_id}`}>
                <td>
                  <div className="flex items-center gap-2.5">
                    <span className="h-6 w-6 rounded-full bg-brand-violet text-white text-[10px] font-bold flex items-center justify-center">
                      {(m.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="font-medium text-brand-ink">{m.name || "—"}</span>
                    {m.user_id === user?.id && (
                      <span className="mono-num text-[9px] chip chip-violet">you</span>
                    )}
                  </div>
                </td>
                <td className="mono-num text-[12px] text-brand-muted">{m.email}</td>
                <td><span className="chip">{m.role_name || "—"}</span></td>
                <td className="mono-num text-[11.5px] text-brand-muted">
                  {m.last_login_at ? new Date(m.last_login_at).toISOString().slice(0, 10) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* Invites list */}
      {data.invites?.length > 0 && (
        <div className="mt-6">
          <div className="micro-label">Pending invites</div>
          <div className="mt-2 card-panel overflow-hidden">
            <div className="table-scroll"><table className="fable-table" data-testid="invites-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th style={{width: "14%"}}>Role</th>
                  <th style={{width: "14%"}}>Sent</th>
                  {canManage && <th style={{width: "8%"}}></th>}
                </tr>
              </thead>
              <tbody>
                {data.invites.map((inv) => (
                  <tr key={inv.id} data-testid={`invite-row-${inv.id}`}>
                    <td className="mono-num text-[12px] text-brand-ink">{inv.email}</td>
                    <td><span className="chip">{inv.role_name || "—"}</span></td>
                    <td className="mono-num text-[11.5px] text-brand-muted">
                      {inv.created_at?.slice(0, 10)}
                    </td>
                    {canManage && (
                      <td className="text-right">
                        <button className="btn-ghost !h-7 !px-2"
                                onClick={() => revokeInvite(inv.id)}
                                data-testid={`invite-revoke-${inv.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </div>
      )}

      {/* Member detail drawer */}
      <DetailDrawer
        open={!!activeMember}
        onOpenChange={(v) => { if (!v) closeDrawer(); }}
        title={activeMember?.name || "Member"}
        subtitle={activeMember?.role_name}
        testid="member-drawer"
      >
        {activeMember && (
          <MemberDetail
            key={activeMember.user_id}
            m={activeMember}
            roles={roles}
            canManage={canManage}
            isSelf={activeMember.user_id === user?.id}
            onChanged={load}
            onRemoved={() => { closeDrawer(); load(); }}
            companyId={companyId}
          />
        )}
      </DetailDrawer>
    </div>
  );
}

function MemberDetail({ m, roles, canManage, isSelf, onChanged, onRemoved, companyId }) {
  const { has, entitlements } = useCompany();
  const canHRManage = has("hr.manage");
  const canHRView = has("hr.view_all");
  const hrEnabled = !!entitlements?.modules?.hr;
  const [tab, setTab] = React.useState("overview");
  const [roleId, setRoleId] = React.useState(m.role_id || "");
  const [saving, setSaving] = React.useState(false);

  async function save() {
    if (roleId === m.role_id || !roleId || saving) return;
    setSaving(true);
    try {
      await api.patch(`/companies/${companyId}/members/${m.user_id}`, { role_id: roleId });
      toast.success("Updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  }

  async function remove() {
    const confirm = window.confirm(isSelf ? "Remove yourself from this company?" : `Remove ${m.name}?`);
    if (!confirm) return;
    try {
      await api.delete(`/companies/${companyId}/members/${m.user_id}`);
      toast.success("Removed");
      onRemoved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  return (
    <div>
      <div className="flex items-center gap-1 px-5 pt-3 border-b border-brand-rule">
        <DrawerTab v="overview" cur={tab} setV={setTab}>Overview</DrawerTab>
        <DrawerTab v="sites" cur={tab} setV={setTab}>Sites</DrawerTab>
        {hrEnabled && <DrawerTab v="hr" cur={tab} setV={setTab}>HR</DrawerTab>}
        {hrEnabled && <DrawerTab v="records" cur={tab} setV={setTab}>Records</DrawerTab>}
      </div>
      {tab === "overview" && (
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={m.email} mono />
            <Field label="Member since" value={m.added_at?.slice(0, 10)} mono />
            <Field label="Last login" value={m.last_login_at?.slice(0, 10) || "—"} mono />
            <Field label="User id" value={m.user_id.slice(0, 12) + "…"} mono />
          </div>
          <div>
            <div className="micro-label">Role</div>
            <div className="mt-1 flex items-center gap-2">
              <select
                className="field-input flex-1"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                disabled={!canManage}
                data-testid="member-drawer-role"
              >
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              {canManage && (
                <button className="btn-violet" disabled={saving || roleId === m.role_id} onClick={save}
                        data-testid="member-drawer-save-role">
                  {saving ? "…" : "Save"}
                </button>
              )}
            </div>
            {!canManage && (
              <div className="mt-1 text-[11px] text-brand-muted">Read-only — you lack <span className="mono-num">members.manage</span>.</div>
            )}
          </div>
          {canManage && !isSelf && (
            <div className="pt-3 border-t border-brand-rule">
              <AdminResetLink companyId={companyId} member={m} />
            </div>
          )}
          {canManage && (
            <div className="pt-3 border-t border-brand-rule">
              <button onClick={remove}
                      className="btn-outline text-brand-coral border-brand-coral/40 hover:!border-brand-coral"
                      data-testid="member-drawer-remove">
                <UserMinus size={13} /> {isSelf ? "Remove myself" : "Remove member"}
              </button>
              {isSelf && (
                <div className="mt-1 text-[11px] text-brand-muted">
                  Cannot leave if you're the last holder of a critical capability.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {tab === "sites" && (
        <MemberSitesTab companyId={companyId} member={m} canManage={canManage} onChanged={onChanged} />
      )}
      {tab === "hr" && hrEnabled && (
        <MemberHRTab companyId={companyId} member={m} canEdit={canHRManage} canView={canHRView || isSelf} />
      )}
      {tab === "records" && hrEnabled && (
        <div className="p-5" data-testid="member-drawer-records">
          <RecordsPanel
            companyId={companyId}
            scope="member"
            subjectId={m.user_id}
            canEdit={canHRManage}
            hideAdd={false}
            testidPrefix="member-records"
          />
        </div>
      )}
    </div>
  );
}

function DrawerTab({ v, cur, setV, children }) {
  const active = v === cur;
  return (
    <button className={`px-3 py-2 text-[12.5px] font-medium border-b-2 ${active ? 'border-brand-violet text-brand-violet' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}
            onClick={() => setV(v)} data-testid={`drawer-tab-${v}`}>
      {children}
    </button>
  );
}

function MemberSitesTab({ companyId, member, canManage, onChanged }) {
  const [sites, setSites] = React.useState([]);
  const [selected, setSelected] = React.useState(new Set(member.site_ids || []));
  const [home, setHome] = React.useState(member.home_site_id || "");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get(`/companies/${companyId}/sites`).then(({data}) => setSites(data.sites));
  }, [companyId]);

  function toggle(id) {
    if (!canManage) return;
    const next = new Set(selected);
    if (next.has(id)) { next.delete(id); if (home === id) setHome(""); }
    else next.add(id);
    setSelected(next);
  }
  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      await api.patch(`/companies/${companyId}/members/${member.user_id}/sites`, {
        site_ids: Array.from(selected),
        home_site_id: home || null,
      });
      toast.success("Sites saved");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  const dirty = home !== (member.home_site_id || "") || setsDiffer(selected, new Set(member.site_ids || []));

  return (
    <div className="p-5 space-y-3" data-testid="member-drawer-sites">
      <div className="micro-label">Assigned sites</div>
      <div className="space-y-1">
        {sites.map((s) => (
          <label key={s.id}
                 className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${canManage ? 'hover:bg-brand-hush cursor-pointer' : ''}`}
                 data-testid={`member-site-${s.id}`}>
            <input type="checkbox" className="accent-brand-violet"
                   checked={selected.has(s.id)} disabled={!canManage}
                   onChange={() => toggle(s.id)} />
            <span className="h-3 w-3 rounded-full" style={{background: s.color}} />
            <span className="flex-1 text-[13px] text-brand-ink">{s.name}</span>
            {selected.has(s.id) && (
              <label className="flex items-center gap-1 text-[10.5px] text-brand-muted cursor-pointer">
                <input type="radio" name="home" checked={home === s.id}
                       disabled={!canManage} onChange={() => setHome(s.id)} />
                home
              </label>
            )}
          </label>
        ))}
        {sites.length === 0 && <div className="text-[12px] text-brand-muted px-2 py-3">No sites yet.</div>}
      </div>
      {canManage && (
        <button className="btn-violet" onClick={save} disabled={!dirty || saving}
                data-testid="member-sites-save">
          {saving ? "…" : "Save assignments"}
        </button>
      )}
    </div>
  );
}

function MemberHRTab({ companyId, member, canEdit, canView }) {
  const [p, setP] = React.useState(null);
  const [f, setF] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!canView) return;
    api.get(`/companies/${companyId}/hr/profile/${member.user_id}`)
      .then(({data}) => { setP(data); setF({
        job_title: data.job_title || "",
        employment_type: data.employment_type || "",
        start_date: data.start_date || "",
        hourly_rate_cents: data.hourly_rate_cents ?? "",
        phone: data.phone || "",
        emergency_contact_name: data.emergency_contact?.name || "",
        emergency_contact_phone: data.emergency_contact?.phone || "",
        emergency_contact_rel: data.emergency_contact?.relationship || "",
      }); })
      .catch(() => setP({ error: true }));
  }, [companyId, member.user_id, canView]);

  if (!canView) return <div className="p-5 text-[12px] text-brand-muted">You can view your own HR profile only.</div>;
  if (!p) return <div className="p-5 text-[12px] text-brand-muted">Loading…</div>;

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        job_title: f.job_title, employment_type: f.employment_type || null,
        start_date: f.start_date || null,
        hourly_rate_cents: f.hourly_rate_cents === "" ? null : Number(f.hourly_rate_cents),
        phone: f.phone,
        emergency_contact: {
          name: f.emergency_contact_name, phone: f.emergency_contact_phone,
          relationship: f.emergency_contact_rel,
        },
      };
      await api.patch(`/companies/${companyId}/hr/profile/${member.user_id}`, payload);
      toast.success("HR profile saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div className="p-5 space-y-3" data-testid="member-drawer-hr">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="micro-label">Job title</label>
          <input className="field-input mt-1" value={f.job_title || ""}
                 onChange={(e)=>setF(x=>({...x,job_title:e.target.value}))}
                 disabled={!canEdit} data-testid="hr-job-title" />
        </div>
        <div>
          <label className="micro-label">Employment type</label>
          <select className="field-input mt-1" value={f.employment_type || ""}
                  onChange={(e)=>setF(x=>({...x,employment_type:e.target.value}))}
                  disabled={!canEdit} data-testid="hr-emp-type">
            <option value="">—</option>
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="casual">Casual</option>
          </select>
        </div>
        <div>
          <label className="micro-label">Start date</label>
          <input type="date" className="field-input mt-1 mono-num" value={f.start_date || ""}
                 onChange={(e)=>setF(x=>({...x,start_date:e.target.value}))}
                 disabled={!canEdit} data-testid="hr-start-date" />
        </div>
        <div>
          <label className="micro-label">Hourly rate (£)</label>
          <input type="number" step="0.01" min="0"
                 className="field-input mt-1 mono-num"
                 value={f.hourly_rate_cents === "" ? "" : (Number(f.hourly_rate_cents)/100).toFixed(2)}
                 onChange={(e)=>setF(x=>({...x,hourly_rate_cents: e.target.value === "" ? "" : Math.round(parseFloat(e.target.value)*100)}))}
                 disabled={!canEdit} data-testid="hr-hourly-rate" />
        </div>
        <div className="col-span-2">
          <label className="micro-label">Phone</label>
          <input className="field-input mt-1 mono-num" value={f.phone || ""}
                 onChange={(e)=>setF(x=>({...x,phone:e.target.value}))}
                 disabled={!canEdit} data-testid="hr-phone" />
        </div>
        <div>
          <label className="micro-label">Emergency name</label>
          <input className="field-input mt-1" value={f.emergency_contact_name || ""}
                 onChange={(e)=>setF(x=>({...x,emergency_contact_name:e.target.value}))}
                 disabled={!canEdit} data-testid="hr-em-name" />
        </div>
        <div>
          <label className="micro-label">Relationship</label>
          <input className="field-input mt-1" value={f.emergency_contact_rel || ""}
                 onChange={(e)=>setF(x=>({...x,emergency_contact_rel:e.target.value}))}
                 disabled={!canEdit} data-testid="hr-em-rel" />
        </div>
        <div className="col-span-2">
          <label className="micro-label">Emergency phone</label>
          <input className="field-input mt-1 mono-num" value={f.emergency_contact_phone || ""}
                 onChange={(e)=>setF(x=>({...x,emergency_contact_phone:e.target.value}))}
                 disabled={!canEdit} data-testid="hr-em-phone" />
        </div>
      </div>
      {canEdit && (
        <button className="btn-violet" onClick={save} disabled={saving} data-testid="hr-save">
          {saving ? "…" : "Save HR profile"}
        </button>
      )}

      {/* Phase 10.1 — rate history + leave balance */}
      <div className="pt-4 border-t border-brand-rule">
        <RateHistoryPanel companyId={companyId} userId={member.user_id}
                          canManage={canEdit} canView={canView}
                          testidPrefix="member-drawer-rates" />
      </div>
      <div className="pt-4 border-t border-brand-rule">
        <LeaveBalancePanel companyId={companyId} userId={member.user_id}
                           canManage={canEdit} canView={canView}
                           testidPrefix="member-drawer-balance" />
      </div>

      {/* Phase 12.1 — effective-dated record + assignments */}
      <div className="pt-4 border-t border-brand-rule">
        <AsOfRecordPanel companyId={companyId} userId={member.user_id} />
      </div>
      <div className="pt-4 border-t border-brand-rule">
        <AssignmentsPanel companyId={companyId} userId={member.user_id}
                          canManage={canEdit} />
      </div>
      <div className="pt-4 border-t border-brand-rule">
        <AttributeHistoryPanel companyId={companyId} userId={member.user_id} />
      </div>
    </div>
  );
}

function setsDiffer(a, b) {
  if (a.size !== b.size) return true;
  for (const x of a) if (!b.has(x)) return true;
  return false;
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 text-[12.5px] text-brand-ink ${mono ? 'mono-num' : ''}`}>{value || "—"}</div>
    </div>
  );
}


/**
 * AdminResetLink — Phase 16.3 admin-assisted reset.
 *
 * Gated by `members.manage` in the parent (button is not rendered otherwise).
 * Never sets a password directly — issues a 15-minute single-use link.  The
 * raw link is shown ONCE with a copy button.  Optionally the reset email is
 * also delivered to the member (outbox in dev, Resend in prod).
 */
function AdminResetLink({ companyId, member }) {
  const [busy, setBusy] = React.useState(false);
  const [sendEmail, setSendEmail] = React.useState(true);
  const [result, setResult] = React.useState(null);

  async function issue() {
    if (busy) return;
    if (!window.confirm(
      `Issue a password-reset link for ${member.name || member.email}?\n\n` +
      `The link is single-use, expires in 15 minutes, and will ` +
      `${sendEmail ? "also be emailed" : "NOT be emailed"} to them.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/members/${member.user_id}/reset-link`,
        { send_email: sendEmail },
      );
      setResult(data);
      toast.success(sendEmail ? "Reset link issued + emailed" : "Reset link issued");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    if (!result?.link) return;
    navigator.clipboard.writeText(result.link);
    toast.success("Reset link copied to clipboard");
  }

  return (
    <div data-testid="member-drawer-admin-reset">
      <div className="micro-label">Password reset (admin-assisted)</div>
      <div className="mt-1 text-[11.5px] text-brand-muted leading-relaxed">
        Issues a single-use link that expires in <span className="mono-num">15 minutes</span>.
        Every issue is stamped in the audit log with your name.
      </div>
      {!result ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-[11.5px] text-brand-ink">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                   className="accent-brand-violet"
                   data-testid="admin-reset-send-email" />
            Also email the link to {member.email}
          </label>
          <button onClick={issue} disabled={busy} className="btn-outline"
                  data-testid="admin-reset-issue">
            <KeyRound size={13} /> {busy ? "Issuing…" : "Send reset link"}
          </button>
        </div>
      ) : (
        <div className="mt-2 rounded-md border border-brand-violet/40 bg-brand-violet/5 p-3"
             data-testid="admin-reset-result">
          <div className="text-[12px] font-semibold text-brand-violet flex items-center gap-1">
            <Mail size={12} /> Copy this link now — it won't be shown again.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="mono-num text-[11px] bg-card border border-brand-rule rounded px-2 py-1 flex-1 break-all"
                  data-testid="admin-reset-link">{result.link}</code>
            <button type="button" onClick={copy} className="btn-outline !h-7"
                    data-testid="admin-reset-copy">
              <Copy size={12} /> Copy
            </button>
          </div>
          <div className="mt-2 text-[11px] text-brand-muted">
            Expires in <span className="mono-num">{result.ttl_minutes}</span> minutes.
            {result.email_sent
              ? " Email also sent."
              : " Not emailed — share the link out-of-band."}
          </div>
          <div className="mt-2">
            <button className="btn-ghost !h-7" onClick={() => setResult(null)}
                    data-testid="admin-reset-close">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
